import { derivePartyDisplayName } from '../../validation/partyNaming.js';
import { optionalBoolean, optionalDate, optionalDigits, optionalNumber, optionalString, pick, requireNumber, } from './legacyRow.js';
const PARTY_ROLES = new Set(['CUSTOMER', 'SUPPLIER']);
const INVOICE_DIRECTIONS = new Set(['IN', 'OUT']);
const INVOICE_ISSUER_KINDS = new Set(['OWN', 'THIRD_PARTY']);
const INVOICE_STATUSES = new Set(['draft', 'authorized', 'cancelled']);
function parsePartyType(value, cpf, cnpj) {
    const raw = String(value ?? '')
        .trim()
        .toUpperCase();
    if (raw === 'PF' || raw === 'PJ')
        return raw;
    if (cpf)
        return 'PF';
    if (cnpj)
        return 'PJ';
    throw new Error('[legacyRowMappers] Party row is missing required field "partyType".');
}
/**
 * Accepts CSV (`CUSTOMER,SUPPLIER`), array, or a single role string.
 * Unknown tokens are dropped; empty result defaults to `['CUSTOMER']`.
 */
function parseRoles(value) {
    let parts = [];
    if (Array.isArray(value)) {
        parts = value.map((v) => String(v));
    }
    else if (value !== undefined && value !== null && value !== '') {
        parts = String(value).split(/[,|;]/);
    }
    const roles = [
        ...new Set(parts
            .map((p) => p.trim().toUpperCase())
            .filter((p) => PARTY_ROLES.has(p))),
    ];
    return roles.length > 0 ? roles : ['CUSTOMER'];
}
/**
 * Canonical naming invariant (`derivePartyDisplayName`) plus legacy-only fallbacks
 * for ERP rows whose name fields are all blank — the document, then the row id.
 */
function legacyPartyDisplayName(input) {
    const canonical = derivePartyDisplayName(input);
    if (canonical)
        return canonical;
    const doc = input.partyType === 'PJ' ? input.cnpj : input.cpf;
    if (doc)
        return doc;
    return `#${input.id}`;
}
function parseInvoiceDirection(value) {
    const raw = String(value ?? '')
        .trim()
        .toUpperCase();
    if (INVOICE_DIRECTIONS.has(raw))
        return raw;
    throw new Error('[legacyRowMappers] Invoice row is missing required field "direction".');
}
function parseInvoiceIssuerKind(value) {
    if (value === undefined)
        return undefined;
    if (value === null || value === '')
        return null;
    const raw = String(value).trim().toUpperCase();
    if (INVOICE_ISSUER_KINDS.has(raw))
        return raw;
    return null;
}
function parseInvoiceStatus(value) {
    const raw = String(value ?? '')
        .trim()
        .toLowerCase();
    if (INVOICE_STATUSES.has(raw))
        return raw;
    return 'authorized';
}
/**
 * Maps a legacy row to our Product type. Handles lowercased keys from Firebird
 * and defensive coercion for numeric/boolean fields.
 */
export function toProduct(row) {
    const id = pick(row, 'id');
    if (id == null || id === '') {
        throw new Error('[legacyRowMappers] Product row is missing required field "id".');
    }
    return {
        id: String(id),
        code: Number(pick(row, 'code')),
        reference: String(pick(row, 'reference') ?? ''),
        barcode: optionalString(pick(row, 'barcode')),
        description: String(pick(row, 'description') ?? ''),
        unit: String(pick(row, 'unit') ?? ''),
        ncm: optionalDigits(pick(row, 'ncm')),
        salePrice: optionalNumber(pick(row, 'salePrice')),
        purchasePrice: optionalNumber(pick(row, 'purchasePrice')),
        unitWeight: optionalNumber(pick(row, 'unitWeight')),
        grossWeight: optionalNumber(pick(row, 'grossWeight')),
        productGroupId: optionalString(pick(row, 'productGroupId')),
        brandId: optionalString(pick(row, 'brandId')),
        defaultSupplierId: optionalString(pick(row, 'defaultSupplierId')),
        imageUrl: optionalString(pick(row, 'imageUrl')),
        grammage: optionalNumber(pick(row, 'grammage')),
        active: optionalBoolean(pick(row, 'active')),
        stockControlEnabled: optionalBoolean(pick(row, 'stockControlEnabled')),
        stockControlByLot: optionalBoolean(pick(row, 'stockControlByLot')),
        stockHasExpiry: optionalBoolean(pick(row, 'stockHasExpiry')),
        stockControlByVolume: optionalBoolean(pick(row, 'stockControlByVolume')),
    };
}
/**
 * Maps a legacy row to our Party type. Handles lowercased keys from Firebird,
 * CSV/`array` roles from the VH query, and digits-only CPF/CNPJ.
 */
export function toParty(row) {
    const id = pick(row, 'id');
    if (id == null || id === '') {
        throw new Error('[legacyRowMappers] Party row is missing required field "id".');
    }
    const cpf = optionalDigits(pick(row, 'cpf'));
    const cnpj = optionalDigits(pick(row, 'cnpj'));
    const partyType = parsePartyType(pick(row, 'partyType'), cpf, cnpj);
    const tradeName = partyType === 'PJ' ? optionalString(pick(row, 'tradeName')) : undefined;
    const legalName = partyType === 'PJ' ? optionalString(pick(row, 'legalName')) : undefined;
    const displayName = legacyPartyDisplayName({
        id: String(id),
        partyType,
        displayName: String(pick(row, 'displayName') ?? ''),
        tradeName,
        legalName,
        cpf,
        cnpj,
    });
    return {
        id: String(id),
        code: Number(pick(row, 'code')),
        partyType,
        displayName,
        roles: parseRoles(pick(row, 'roles')),
        cpf: partyType === 'PF' ? cpf : undefined,
        birthDate: optionalDate(pick(row, 'birthDate')),
        cnpj: partyType === 'PJ' ? cnpj : undefined,
        tradeName,
        legalName,
        email: optionalString(pick(row, 'email')),
        phone: optionalString(pick(row, 'phone')),
        // Address in **text mode** (spec 0047 §4.2 / D24): `validateParty` does not
        // demand cityId/stateId, so an import is never refused for municipality.
        // IBGE codes (D31 — values, not FKs to a local catalog) are read when the
        // select sends them; omitted columns stay undefined → sync skips the field.
        addressZipCode: optionalString(pick(row, 'addressZipCode')),
        addressStreet: optionalString(pick(row, 'addressStreet')),
        addressNumber: optionalString(pick(row, 'addressNumber')),
        addressComplement: optionalString(pick(row, 'addressComplement')),
        addressDistrict: optionalString(pick(row, 'addressDistrict')),
        addressState: optionalString(pick(row, 'addressState')),
        addressCity: optionalString(pick(row, 'addressCity')),
        addressCityId: optionalString(pick(row, 'addressCityId')),
        addressStateId: optionalString(pick(row, 'addressStateId')),
        notes: optionalString(pick(row, 'notes')),
        inclusionDate: optionalDate(pick(row, 'inclusionDate')) ?? undefined,
        active: optionalBoolean(pick(row, 'active')),
        inactivationDate: optionalDate(pick(row, 'inactivationDate')),
    };
}
/**
 * Maps a legacy row to our Invoice type (NOTAS → fiscal header).
 */
export function toInvoice(row) {
    const id = pick(row, 'id');
    if (id == null || id === '') {
        throw new Error('[legacyRowMappers] Invoice row is missing required field "id".');
    }
    const number = String(pick(row, 'number') ?? '').trim();
    if (!number) {
        throw new Error('[legacyRowMappers] Invoice row is missing required field "number".');
    }
    const partyId = String(pick(row, 'partyId') ?? '').trim();
    if (!partyId) {
        throw new Error('[legacyRowMappers] Invoice row is missing required field "partyId".');
    }
    return {
        id: String(id),
        number,
        series: optionalString(pick(row, 'series')),
        accessKey: optionalString(pick(row, 'accessKey')),
        protocol: optionalString(pick(row, 'protocol')),
        legacyType: optionalNumber(pick(row, 'legacyType')),
        direction: parseInvoiceDirection(pick(row, 'direction')),
        issuerKind: parseInvoiceIssuerKind(pick(row, 'issuerKind')),
        partyId,
        issueDate: optionalDate(pick(row, 'issueDate')),
        entryDate: optionalDate(pick(row, 'entryDate')),
        cfop: optionalString(pick(row, 'cfop')),
        cfopComplement: optionalString(pick(row, 'cfopComplement')),
        state: optionalString(pick(row, 'state')),
        freightType: optionalString(pick(row, 'freightType')),
        paymentTerm: optionalString(pick(row, 'paymentTerm')),
        paymentMethod: optionalString(pick(row, 'paymentMethod')),
        carrierId: optionalString(pick(row, 'carrierId')),
        salespersonId: optionalString(pick(row, 'salespersonId')),
        customerOrderRef: optionalString(pick(row, 'customerOrderRef')),
        notes: optionalString(pick(row, 'notes')),
        productsAmount: optionalNumber(pick(row, 'productsAmount')) ?? undefined,
        discountAmount: optionalNumber(pick(row, 'discountAmount')),
        freightAmount: optionalNumber(pick(row, 'freightAmount')),
        insuranceAmount: optionalNumber(pick(row, 'insuranceAmount')),
        otherExpensesAmount: optionalNumber(pick(row, 'otherExpensesAmount')),
        icmsAmount: optionalNumber(pick(row, 'icmsAmount')),
        icmsStAmount: optionalNumber(pick(row, 'icmsStAmount')),
        ipiAmount: optionalNumber(pick(row, 'ipiAmount')),
        pisAmount: optionalNumber(pick(row, 'pisAmount')),
        cofinsAmount: optionalNumber(pick(row, 'cofinsAmount')),
        issAmount: optionalNumber(pick(row, 'issAmount')),
        totalAmount: requireNumber(pick(row, 'totalAmount'), 'totalAmount'),
        itemCount: optionalNumber(pick(row, 'itemCount')) ?? undefined,
        status: parseInvoiceStatus(pick(row, 'status')),
        netWeight: optionalNumber(pick(row, 'netWeight')),
        grossWeight: optionalNumber(pick(row, 'grossWeight')),
        volumeCount: optionalNumber(pick(row, 'volumeCount')),
        inclusionDate: optionalDate(pick(row, 'inclusionDate')) ?? undefined,
    };
}
/**
 * Maps a legacy row to our InvoiceItem type (NOTAI → fiscal line).
 */
export function toInvoiceItem(row) {
    const id = pick(row, 'id');
    if (id == null || id === '') {
        throw new Error('[legacyRowMappers] InvoiceItem row is missing required field "id".');
    }
    const invoiceId = String(pick(row, 'invoiceId') ?? '').trim();
    if (!invoiceId) {
        throw new Error('[legacyRowMappers] InvoiceItem row is missing required field "invoiceId".');
    }
    const productId = String(pick(row, 'productId') ?? '').trim();
    if (!productId) {
        throw new Error('[legacyRowMappers] InvoiceItem row is missing required field "productId".');
    }
    const description = String(pick(row, 'description') ?? '').trim();
    if (!description) {
        throw new Error('[legacyRowMappers] InvoiceItem row is missing required field "description".');
    }
    const quantity = requireNumber(pick(row, 'quantity'), 'quantity');
    const unitPrice = requireNumber(pick(row, 'unitPrice'), 'unitPrice');
    const totalPrice = optionalNumber(pick(row, 'totalPrice')) ?? quantity * unitPrice;
    return {
        id: String(id),
        invoiceId,
        lineNumber: requireNumber(pick(row, 'lineNumber'), 'lineNumber'),
        productId,
        description,
        unit: optionalString(pick(row, 'unit')),
        quantity,
        unitPrice,
        baseQuantity: optionalNumber(pick(row, 'baseQuantity')),
        baseUnit: optionalString(pick(row, 'baseUnit')),
        conversionFactor: optionalNumber(pick(row, 'conversionFactor')),
        totalPrice,
        discountAmount: optionalNumber(pick(row, 'discountAmount')),
        cfop: optionalString(pick(row, 'cfop')),
        icmsRate: optionalNumber(pick(row, 'icmsRate')),
        icmsAmount: optionalNumber(pick(row, 'icmsAmount')),
        icmsStAmount: optionalNumber(pick(row, 'icmsStAmount')),
        ipiRate: optionalNumber(pick(row, 'ipiRate')),
        ipiAmount: optionalNumber(pick(row, 'ipiAmount')),
        pisAmount: optionalNumber(pick(row, 'pisAmount')),
        cofinsAmount: optionalNumber(pick(row, 'cofinsAmount')),
        costAmount: optionalNumber(pick(row, 'costAmount')),
        warehouseId: optionalString(pick(row, 'warehouseId')),
        purchaseOrderRef: optionalString(pick(row, 'purchaseOrderRef')),
        purchaseOrderItemRef: optionalString(pick(row, 'purchaseOrderItemRef')),
        lot: optionalString(pick(row, 'lot')),
        expiryDate: optionalDate(pick(row, 'expiryDate')),
        width: optionalNumber(pick(row, 'width')),
        height: optionalNumber(pick(row, 'height')),
    };
}
//# sourceMappingURL=legacyRowMappers.js.map